import type { CommercialTermsProduct, PerUnitProduct } from '@vy/services';
import {
  applyDiscount,
  findErpEntityInIntegrationEntities,
  getBillingCyclesPerDurationInMonths,
  getFormattedDiscountForProduct,
  isCommercialTermsProduct,
  isFixedDeductionProductOnly,
  isPercentageDeductionProductOnly,
  isScheduledInstallmentsProduct,
  isSubscriptionProduct,
} from '@vy/services';
import type {
  BaseIntegrationEntity,
  CatalogProducts,
  Currency,
  ExternalProductIntegration,
  FixedPricing,
  PaymentTerm,
  Plan,
  PlanDuration,
} from '@vy/types';
import { DEFAULT_CURRENCY, PAYMENT_TERMS, PRICING_MODELS, SYNC_STATUSES, type FixedProduct } from '@vy/types';
import {
  fromNullToUndefined,
  isNotNullOrUndefined,
  now,
  Optional,
  raiseUnreachable,
  withNewOptionalValue,
} from '@vy/utils';
import { formatDate } from '../../../../../utils/date';
import { resolveAnrokFields } from '../../shared/resolve-anrok-fields';
import type { ContractFixedFeesColumns } from '../contract-fixed-fees-types/contract-fixed-fees-columns';

export function buildFixedFeesRows(
  fixedProducts: FixedProduct[],
  plan: Plan,
  currency?: Currency,
  catalogProductsById?: Map<string, CatalogProducts>,
): ContractFixedFeesColumns[] {
  return buildFixedProductsRows(fixedProducts, plan.billingData.duration, currency, catalogProductsById);
}

function buildFixedProductsRows(
  products: FixedProduct[],
  planDuration: PlanDuration,
  currency?: Currency,
  catalogProductsById?: Map<string, CatalogProducts>,
): ContractFixedFeesColumns[] {
  return products.flatMap((product) =>
    product.variants.map((variant) => {
      const { anrokId, anrokOverrideId, anrokCatalogDefaultId } = resolveAnrokFields(
        product,
        catalogProductsById,
      );

      return {
        id: product.id,
        productName: variant.name,
        pricingType: getFixedProductLabel(variant.pricing),
        amount: getProductPrice(product),
        quantity: getProductQuantity(product),
        billingInterval: product.billingInterval,
        paymentTerm: product.paymentTerm,
        startDate: formatDate(product.updatedAt),
        scheduleStartDate: withNewOptionalValue(product.startDate, formatDate),
        scheduleEndDate: withNewOptionalValue(product.endDate, formatDate),
        installments: calculateInstallments(product, planDuration),
        shouldShowInstallments: shouldShowInstallments(product, planDuration),
        proRate: product.rateMultiplier,
        pricePerUnit: calculatePricePerUnit(product),
        erp: getErpProduct(product.integrationEntities, product.externalIntegration),
        anrokId,
        anrokOverrideId,
        anrokCatalogDefaultId,
        marketplaceProductId: product.cloudProviderSettings?.cloudProviderMarketplaceId,
        marketplaceDimension: product.cloudProviderSettings?.dimension,
        discount: getFormattedDiscountForProduct(product, currency ?? DEFAULT_CURRENCY),
        product,
      };
    }),
  );
}

export function buildMeteredCreditRows(
  products: PerUnitProduct[],
  catalogProductsById?: Map<string, CatalogProducts>,
): ContractFixedFeesColumns[] {
  return products.flatMap((product) =>
    product.variants.map((variant) => {
      const { anrokId, anrokOverrideId, anrokCatalogDefaultId } = resolveAnrokFields(
        product,
        catalogProductsById,
      );

      return {
        id: product.id,
        productName: variant.name,
        pricingType: 'Credits',
        amount: variant.pricing.priceConfiguration.price,
        billingInterval: product.billingInterval,
        startDate: formatDate(product.updatedAt),
        proRate: undefined,
        pricePerUnit: undefined,
        paymentTerm: undefined,
        erp: getErpProduct(product.integrationEntities, product.externalIntegration),
        anrokId,
        anrokOverrideId,
        anrokCatalogDefaultId,
        marketplaceProductId: product.cloudProviderSettings?.cloudProviderMarketplaceId,
        marketplaceDimension: product.cloudProviderSettings?.dimension,
        product,
      };
    }),
  );
}

export function formatPaymentLabel(paymentTerm: PaymentTerm) {
  switch (paymentTerm) {
    case PAYMENT_TERMS.Postpayment:
      return 'Post Payment';
    case PAYMENT_TERMS.Prepayment:
      return 'Pre Payment';
    default:
      return raiseUnreachable(paymentTerm);
  }
}

export function getErpProduct(
  integrationEntities: BaseIntegrationEntity[] | undefined,
  externalIntegration: Optional<ExternalProductIntegration>,
): Optional<BaseIntegrationEntity> {
  if (isNotNullOrUndefined(externalIntegration)) {
    const displayId = fromNullToUndefined(externalIntegration.code);

    return {
      integrationProvider: externalIntegration.type,
      integrationExternalId: externalIntegration.id,
      syncStatus: SYNC_STATUSES.Synced,
      updatedAt: now(),
      metadata: { displayId },
    };
  }

  return findErpEntityInIntegrationEntities(integrationEntities ?? []);
}

function calculatePricePerUnit(product: FixedProduct) {
  return isSubscriptionProduct(product) ? product.variants[0].pricing.priceConfiguration.price : undefined;
}

function shouldSplitAmount(product: CommercialTermsProduct) {
  return product.variants[0].pricing.priceConfiguration.shouldSplitAmount;
}

function calculateInstallments(product: FixedProduct, planDuration: PlanDuration) {
  if (isCommercialTermsProduct(product) && shouldSplitAmount(product)) {
    return getBillingCyclesPerDurationInMonths(product.billingInterval, planDuration);
  }

  if (isScheduledInstallmentsProduct(product)) {
    return product.variants[0].pricing.priceConfiguration.scheduledInstallments?.installments.length;
  }

  if (isCommercialTermsProduct(product)) {
    return product.variants[0].pricing.priceConfiguration.installments;
  }

  if (isSubscriptionProduct(product)) {
    return product.variants[0].pricing.priceConfiguration.installments;
  }

  if (isPercentageDeductionProductOnly(product) || isFixedDeductionProductOnly(product)) {
    return product.variants[0].pricing.priceConfiguration.installments;
  }

  return undefined;
}

function shouldShowInstallments(product: FixedProduct, planDuration: PlanDuration) {
  return isNotNullOrUndefined(calculateInstallments(product, planDuration));
}

function getProductPrice(product: FixedProduct) {
  const { pricingModel, priceConfiguration } = product.variants[0].pricing;
  switch (pricingModel) {
    case PRICING_MODELS.Commission:
      return `${priceConfiguration.percentage} %`;
    case PRICING_MODELS.PercentageDeduction:
      return `${priceConfiguration.percentageToDeduct} %`;
    case PRICING_MODELS.FixedDeduction:
      return priceConfiguration.amountToDeduct;
    case PRICING_MODELS.FixedOneTime:
    case PRICING_MODELS.CommercialTerms:
    case PRICING_MODELS.Refund:
      return priceConfiguration.price;
    case PRICING_MODELS.Subscription:
      return calculateSubscriptionPrice(product);
    default:
      return raiseUnreachable(pricingModel);
  }
}

function calculateSubscriptionPrice(product: FixedProduct) {
  if (!isSubscriptionProduct(product)) {
    return undefined;
  }
  const { price, seats, installments } = product.variants[0].pricing.priceConfiguration;
  const totalBeforeDiscount = price * (installments ?? 1) * (seats ?? 1);

  return applyDiscount(product, totalBeforeDiscount);
}

function getProductQuantity(product: FixedProduct) {
  switch (product.variants[0].pricing.pricingModel) {
    case PRICING_MODELS.Subscription:
      return product.variants[0].pricing.priceConfiguration.seats ?? 1;
    default:
      return undefined;
  }
}

export const ONE_TIME_FEE_LABEL = 'One-Time Fee';

export function getFixedProductLabel(pricing: FixedPricing) {
  switch (pricing.pricingModel) {
    case PRICING_MODELS.PercentageDeduction:
    case PRICING_MODELS.FixedDeduction:
      return 'Discount';
    case PRICING_MODELS.FixedOneTime:
      return ONE_TIME_FEE_LABEL;
    case PRICING_MODELS.Subscription:
      return 'Subscription';
    case PRICING_MODELS.CommercialTerms:
      return 'Contract Terms';
    case PRICING_MODELS.Commission:
      return 'Commission';
    case PRICING_MODELS.Refund:
      return 'Refund';
    default:
      return raiseUnreachable(pricing);
  }
}
